import React from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Form } from '@themesberg/react-bootstrap';
import { END_POINT_DEPARTMENT } from "../const";
import { showSuccessToast } from "../utils/toast";
import EditFormLayout from "../components/EditFormLayout";
import RecordEditActions from "../components/RecordEditActions";
import RecordEditFeedback from "../components/RecordEditFeedback";
import CompanyCheckboxPicker from "../components/CompanyCheckboxPicker";
import useRecordEditor from "../hooks/useRecordEditor";

const RELATED_ENDPOINTS = { context: 'auth/context' };

export const DepartmentEditForm = ({ departmentId, onSuccess, onCancel, onSubmittingChange }) => {
  const editor = useRecordEditor({
    recordId: departmentId,
    endpoint: END_POINT_DEPARTMENT,
    label: "department",
    relatedEndpoints: RELATED_ENDPOINTS,
    onSuccess,
    onSubmittingChange,
  });
  const department = editor.record;
  const companies = editor.related.context?.companies || [];
  const allowMultipleCompanies = Boolean(editor.related.context?.allow_multiple_companies);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: department?.name || "",
      company_ids: (department?.companies || []).map(company => String(company.id)),
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Department name is required"),
      company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),
    onSubmit: editor.submit,
  });

  if (editor.loading || editor.loadError || !department) {
    return <RecordEditFeedback loading={editor.loading} error={editor.loadError} label="department"
      onRetry={editor.retry} onCancel={onCancel} />;
  }

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group controlId="edit-department-name">
            <Form.Label>Department Name <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" autoFocus disabled={formik.isSubmitting}
              {...formik.getFieldProps("name")} isInvalid={formik.touched.name && formik.errors.name} />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        {(allowMultipleCompanies || companies.length > 1) && (
          <Col md={12} className="mb-3">
            <CompanyCheckboxPicker companies={companies} selectedIds={formik.values.company_ids}
              onChange={companyIds => formik.setFieldValue("company_ids", companyIds)}
              error={formik.touched.company_ids && formik.errors.company_ids} disabled={formik.isSubmitting} />
          </Col>
        )}
      </Row>
      <RecordEditActions formik={formik} backTo="/departments" onCancel={onCancel} />
    </Form>
  );
};

export const EditDepartmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleSuccess = result => {
    showSuccessToast(result.message);
    navigate("/departments");
  };

  return (
    <EditFormLayout title="Edit Department" subtitle="Update department details" backTo="/departments">
      <DepartmentEditForm key={id} departmentId={id} onSuccess={handleSuccess} />
    </EditFormLayout>
  );
};
